import React, { useState } from 'react';

/**
 * DropZone Component
 * A reusable drop target with visual feedback for drag-and-drop operations
 *
 * @param {Object} props
 * @param {function} props.onDrop - Callback when an item is dropped
 * @param {React.ReactNode} props.children - Content to render inside the drop zone
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Additional inline styles
 * @param {boolean} [props.disabled=false] - Whether the drop zone is disabled
 * @param {string} [props.acceptType='creative'] - The type of drag data to accept
 * @param {string} [props.hoverBorderColor] - Border color when dragging over
 */
const DropZone = ({
    onDrop,
    children,
    className = '',
    style = {},
    disabled = false,
    acceptType = 'creative',
    hoverBorderColor = 'var(--color-primary)'
}) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (!isDragOver) {
            setIsDragOver(true);
        }
    };

    const handleDragEnter = (e) => {
        if (disabled) return;
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        if (disabled) return;
        e.preventDefault();

        // Only set to false if we're leaving the drop zone entirely
        // Check if the relatedTarget is a child of this element
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDrop = (e) => {
        if (disabled) return;
        e.preventDefault();
        setIsDragOver(false);

        try {
            const dataType = e.dataTransfer.getData('application/type');
            if (dataType && dataType !== acceptType) {
                return; // Wrong type, ignore
            }

            const data = e.dataTransfer.getData('application/json');
            if (data && onDrop) {
                const parsedData = JSON.parse(data);
                onDrop(parsedData, e);
            }
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    };

    const dropZoneStyle = {
        ...style,
        transition: 'all var(--transition-fast)',
        ...(isDragOver && !disabled ? {
            borderColor: hoverBorderColor,
            borderStyle: 'dashed',
            background: 'var(--color-primary-light)',
            boxShadow: `0 0 0 2px ${hoverBorderColor}20`
        } : {})
    };

    return (
        <div
            className={className}
            style={dropZoneStyle}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {children}
        </div>
    );
};

export default DropZone;
